import { Section, Seat, SeatGridConfig, SeatStatus } from './types';
import { generateSeatId } from './rules';
import { FLOORPLAN_LABELS } from './floorplan.config';

/**
 * Standard seat dimensions constants - responsive for mobile
 */
export const SEAT_DIMENSIONS = {
  WIDTH: 30,
  HEIGHT: 30,
  MOBILE_WIDTH: 35, // Larger for touch targets
  MOBILE_HEIGHT: 35,
} as const;

// Section definitions - Only cabin rooms with visible boundaries
// Open desk areas (OPEN_*) don't have section boxes since they're not enclosed cabins
// SANGAMA removed as it's now a meeting room, not a desk section
export const sections: Section[] = [
  // TUNGA - 6 seater (2-column grid) - height reduced by 30
  {
    id: 'TUNGA',
    name: FLOORPLAN_LABELS.TUNGA,
    pathD: 'M 525 525 L 690 525 L 690 683 L 525 683 Z', // height: 158, end y: 525 + 158 = 683
  },
  // Cuavary - 8 seater (2 rows x 4 columns) - height reduced by 30
  {
    id: 'CAUVERY',
    name: FLOORPLAN_LABELS.CAUVERY,
    pathD: 'M 263 525 L 525 525 L 525 683 L 263 683 Z', // height: 158, end y: 525 + 158 = 683
  },
  // KRISHNA - 4 seater (2 rows x 2 columns) - scaled by 0.75 - beside 2-seater
  {
    id: 'KRISHNA',
    name: FLOORPLAN_LABELS.KRISHNA,
    pathD: 'M 413 188 L 578 188 L 578 338 L 413 338 Z',
  },
  // BHIMA - 4 seater (2 rows x 2 columns) - scaled by 0.75 - beside SANGAMA
  {
    id: 'BHIMA',
    name: FLOORPLAN_LABELS.BHIMA,
    pathD: 'M 413 338 L 578 338 L 578 480 L 413 480 Z',
  },
  // SHARAVATHI - 6 seater (2 rows x 3 columns) - scaled by 0.75
  {
    id: 'SHARAVATHI',
    name: FLOORPLAN_LABELS.SHARAVATHI,
    pathD: 'M 278 0 L 428 0 L 428 143 L 278 143 Z',
  },
  // BHADRA - 6 seater (2 rows x 3 columns) - scaled by 0.75
  {
    id: 'BHADRA',
    name: FLOORPLAN_LABELS.BHADRA,
    pathD: 'M 428 0 L 578 0 L 578 143 L 428 143 Z',
  },
  // VARADAP - 10 seater (2 rows x 5 columns) - scaled by 0.75
  {
    id: 'VARADA',
    name: FLOORPLAN_LABELS.VARADA,
    pathD: 'M 0 0 L 188 0 L 188 270 L 0 270 Z',
  },
];

// Seat grid configurations - Only for open desk areas
// Private rooms don't have selectable seats
const seatGridConfigs: SeatGridConfig[] = [
  // 3-seater room 1 (top row, left of SHARAVATHI) - seats arranged vertically - scaled by 0.75
  {
    sectionId: 'OPEN_3SEATER_1',
    rows: 3,
    cols: 1,
    originX: 218, // 290 * 0.75
    originY: 30, // 40 * 0.75
    seatWidth: 23, // 30 * 0.75
    seatHeight: 23, // 30 * 0.75
    gapX: 0,
    gapY: 15, // 20 * 0.75
    statusDistribution: { available: 3 },
  },
  
  // 3-seater room 2 (top row, rightmost) - seats arranged vertically - scaled by 0.75
  {
    sectionId: 'OPEN_3SEATER_2',
    rows: 3,
    cols: 1,
    originX: 600, // 800 * 0.75
    originY: 30, // 40 * 0.75
    seatWidth: 23, // 30 * 0.75
    seatHeight: 23, // 30 * 0.75
    gapX: 0,
    gapY: 15, // 20 * 0.75
    statusDistribution: { available: 3 },
  },
  
  // 2-seater room (middle row) - seats equidistant and centered - scaled by 0.75
  {
    sectionId: 'OPEN_2SEATER',
    rows: 1,
    cols: 2,
    originX: 289, // 385 * 0.75
    originY: 210, // 280 * 0.75
    seatWidth: 23, // 30 * 0.75
    seatHeight: 23, // 30 * 0.75
    gapX: 45, // 60 * 0.75
    gapY: 0,
    statusDistribution: { available: 2 },
  },
  
  // 4-seater open desk (right side, shares wall with BHIMA/KRISHNA) - seats arranged vertically, equidistant - scaled by 0.75
  {
    sectionId: 'OPEN_4SEATER',
    rows: 4,
    cols: 1,
    originX: 615, // 820 * 0.75
    originY: 210, // 280 * 0.75
    seatWidth: 23, // 30 * 0.75
    seatHeight: 23, // 30 * 0.75
    gapX: 0,
    gapY: 45, // 60 * 0.75
    statusDistribution: { available: 4 },
  },
  
  // TUNGA - 6 seater (2-column grid: 3 rows x 2 columns) - adjusted for reduced height
  {
    sectionId: 'TUNGA',
    rows: 3,
    cols: 2,
    originX: 565, // Center aligned within room
    originY: 548, // Center aligned within room
    seatWidth: 23, // 30 * 0.75
    seatHeight: 23, // 30 * 0.75
    gapX: 40, // Adjusted for new room width
    gapY: 22, // Adjusted for reduced height
    statusDistribution: { available: 6 },
  },
  
  // Cuavary - 8 seater (2 rows x 4 columns grid) - adjusted for reduced height
  {
    sectionId: 'CAUVERY',
    rows: 2,
    cols: 4,
    originX: 297, // Center aligned within room
    originY: 563, // Center aligned within room (adjusted for reduced height)
    seatWidth: 23, // 30 * 0.75
    seatHeight: 23, // 30 * 0.75
    gapX: 34, // Adjusted for proper spacing
    gapY: 37, // Adjusted for reduced height
    statusDistribution: { available: 8 },
  },
  
  // KRISHNA - 4 seater (2 rows x 2 columns grid) - center aligned and equidistant - scaled by 0.75 - beside 2-seater
  {
    sectionId: 'KRISHNA',
    rows: 2,
    cols: 2,
    originX: 456, // 608 * 0.75
    originY: 221, // 295 * 0.75
    seatWidth: 23, // 30 * 0.75
    seatHeight: 23, // 30 * 0.75
    gapX: 30, // 40 * 0.75
    gapY: 38, // 50 * 0.75
    statusDistribution: { available: 4 },
  },
  
  // BHIMA - 4 seater (2 rows x 2 columns grid) - center aligned and equidistant - scaled by 0.75 - beside SANGAMA
  {
    sectionId: 'BHIMA',
    rows: 2,
    cols: 2,
    originX: 456, // 608 * 0.75
    originY: 368, // 490 * 0.75
    seatWidth: 23, // 30 * 0.75
    seatHeight: 23, // 30 * 0.75
    gapX: 30, // 40 * 0.75
    gapY: 38, // 50 * 0.75
    statusDistribution: { available: 4 },
  },
  
  // SHARAVATHI - 6 seater (2 rows x 3 columns grid) - center aligned and equidistant - scaled by 0.75
  {
    sectionId: 'SHARAVATHI',
    rows: 2,
    cols: 3,
    originX: 299, // 398 * 0.75
    originY: 38, // 50 * 0.75
    seatWidth: 23, // 30 * 0.75
    seatHeight: 23, // 30 * 0.75
    gapX: 21, // 28 * 0.75
    gapY: 30, // 40 * 0.75
    statusDistribution: { available: 6 },
  },
  
  // BHADRA - 6 seater (2 rows x 3 columns grid) - center aligned and equidistant - scaled by 0.75
  {
    sectionId: 'BHADRA',
    rows: 2,
    cols: 3,
    originX: 449, // 598 * 0.75
    originY: 38, // 50 * 0.75
    seatWidth: 23, // 30 * 0.75
    seatHeight: 23, // 30 * 0.75
    gapX: 21, // 28 * 0.75
    gapY: 30, // 40 * 0.75
    statusDistribution: { available: 6 },
  },
  
  // VARADA - 10 seater (5 rows x 2 columns grid) - center aligned and equidistant - scaled by 0.75
  {
    sectionId: 'VARADA',
    rows: 5,
    cols: 2,
    originX: 48, // 64 * 0.75
    originY: 26, // 35 * 0.75
    seatWidth: 23, // 30 * 0.75
    seatHeight: 23, // 30 * 0.75
    gapX: 47, // 63 * 0.75
    gapY: 26, // 35 * 0.75
    statusDistribution: { available: 10 },
  },
];

function generateSeatsFromGrid(config: SeatGridConfig): Seat[] {
  const seats: Seat[] = [];
  const totalSeats = config.rows * config.cols;
  
  // Create status array based on distribution
  const statuses: SeatStatus[] = [];
  const dist = config.statusDistribution || {};
  const availableCount = dist.available || totalSeats;
  const occupiedCount = dist.occupied || 0;
  const blockedCount = dist.blocked || 0;
  
  // Fill statuses array
  for (let i = 0; i < availableCount; i++) statuses.push('available');
  for (let i = 0; i < occupiedCount; i++) statuses.push('occupied');
  for (let i = 0; i < blockedCount; i++) statuses.push('blocked');
  
  // Shuffle statuses
  for (let i = statuses.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [statuses[i], statuses[j]] = [statuses[j], statuses[i]];
  }
  
  // Generate seats
  let seatIndex = 0;
  for (let row = 0; row < config.rows; row++) {
    for (let col = 0; col < config.cols; col++) {
      const x = config.originX + col * (config.seatWidth + config.gapX);
      const y = config.originY + row * (config.seatHeight + config.gapY);
      
      seats.push({
        id: generateSeatId(config.sectionId, seatIndex),
        sectionId: config.sectionId,
        x,
        y,
        width: config.seatWidth,
        height: config.seatHeight,
        status: statuses[seatIndex] || 'available',
      });
      
      seatIndex++;
    }
  }
  
  return seats;
}

export function generateSeatMapData(): { sections: Section[]; seats: Seat[] } {
  const allSeats: Seat[] = [];
  
  seatGridConfigs.forEach((config) => {
    const sectionSeats = generateSeatsFromGrid(config);
    allSeats.push(...sectionSeats);
  });
  
  return {
    sections,
    seats: allSeats,
  };
}
